import { useState, useEffect } from 'react';
import { getAllFunds, getSLAConfigByFund, saveSLAConfig } from '../../../services/firestoreService';
import { useAuth } from '../../../hooks/useAuth';
import SelectField from '../../shared/Forms/SelectField';
import Button from '../../shared/UI/Button';

const STAGES = [
  { id: 'pif_analyst_review',  label: 'PIF: Analyst Review' },
  { id: 'pif_peer_review',     label: 'PIF: Peer Review' },
  { id: 'hop_review',          label: 'PIF: Head of Pipeline Review' },
  { id: 'iic_hop_approval',    label: 'IIC: Head of Pipeline Approval' },
  { id: 'iic_cio_approval',    label: 'IIC: CIO Approval' },
  { id: 'iic_md_approval',     label: 'IIC: MD Approval' },
  { id: 'screening',           label: 'Screening Stage' },
  { id: 'dd',                  label: 'Due Diligence Stage' },
  { id: 'ic_round_robin',      label: 'IC Round Robin Vote', defaultDays: 3 },
];

export default function SLAConfig() {
  const { user } = useAuth();
  const [funds, setFunds] = useState([]);
  const [selectedFundId, setSelectedFundId] = useState('');
  const [targets, setTargets] = useState({});
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  // Whether SLA config already exists for the selected fund
  const [configExists, setConfigExists] = useState(false);

  useEffect(() => {
    getAllFunds().then(list => setFunds(list.filter(f => f.is_active !== false)));
  }, []);

  useEffect(() => {
    if (!selectedFundId) { setTargets({}); setConfigExists(false); return; }
    setLoading(true);
    getSLAConfigByFund(selectedFundId)
      .then(configs => {
        const map = {};
        STAGES.forEach(s => { map[s.id] = s.defaultDays || 5; });
        configs.forEach(c => { map[c.stage] = c.target_days; });
        setTargets(map);
        // If Firestore returned any existing SLA records for this fund, it's an update
        setConfigExists(configs.length > 0);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [selectedFundId]);

  async function handleSave() {
    if (!selectedFundId) { setError('Please select a fund first.'); return; }
    setSaving(true);
    setError(null);
    try {
      await Promise.all(
        STAGES.map(stage =>
          saveSLAConfig({
            fundId: selectedFundId,
            stage: stage.id,
            targetDays: parseInt(targets[stage.id], 10) || 1,
            performedBy: user.uid,
          })
        )
      );
      setSuccessMsg('SLA targets saved successfully!');
      setConfigExists(true);
      setTimeout(() => setSuccessMsg(''), 2500);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  const fundOptions = funds.map(f => ({ value: f.id, label: f.name }));

  return (
    <div className="card">
      <div className="card-header"><h2>SLA Configuration</h2></div>
      <div className="card-body">
        {error && <div className="alert alert-error">{error}</div>}
        {successMsg && <div className="alert alert-success">{successMsg}</div>}

        <div style={{ maxWidth: 360, marginBottom: 24 }}>
          <SelectField
            label="Select Fund"
            value={selectedFundId}
            onChange={e => setSelectedFundId(e.target.value)}
            options={fundOptions}
            placeholder="Choose a fund…"
          />
        </div>

        {selectedFundId && !loading && (
          <>
            {/* Update confirmation notice */}
            {configExists && !successMsg && (
              <div className="alert alert-info" style={{ marginBottom: 16 }}>
                SLA configuration already exists for this fund. Saving will update the existing configuration.
              </div>
            )}

            <div style={{ overflowX: 'auto' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>Stage</th>
                    <th>Display Name</th>
                    <th style={{ width: 160 }}>Target Days</th>
                  </tr>
                </thead>
                <tbody>
                  {STAGES.map(stage => (
                    <tr key={stage.id}>
                      <td><code style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{stage.id}</code></td>
                      <td>{stage.label}</td>
                      <td>
                        <input
                          type="number"
                          min="1"
                          className="form-input"
                          style={{ width: 80 }}
                          value={targets[stage.id] ?? (stage.defaultDays || 5)}
                          onChange={e => setTargets(prev => ({ ...prev, [stage.id]: e.target.value }))}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ marginTop: 20 }}>
              <Button variant="primary" loading={saving} onClick={handleSave}>
                {configExists ? 'Update SLA Targets' : 'Save All SLA Targets'}
              </Button>
            </div>
          </>
        )}

        {!selectedFundId && (
          <div className="empty-state">
            <div className="empty-state-icon">⏱️</div>
            <h3>Select a fund</h3>
            <p>Choose a fund to configure SLA targets for each pipeline stage.</p>
          </div>
        )}

        {selectedFundId && loading && (
          <div className="spinner-wrap"><div className="spinner" /></div>
        )}
      </div>
    </div>
  );
}
